import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchUserInteractions } from 'actions/RecommendationActions'

/**
 * Props for useUserInteractions hook.
 * @property {App.UserInteractionType} interactionType - The type of user interaction to fetch.
 * @property {number} [sinceDaysAgo] - Optionally filter interactions to only those since this many days ago.
 * @property {boolean} [disabled] - Whether to disable fetching interactions. Defaults to false.
 */
type Props = {
  interactionType: App.UserInteractionType
  sinceDaysAgo?: number
  disabled?: boolean
}

/**
 * Return type for useUserInteractions hook.
 * @property {boolean} fetching - Whether the interactions are currently being fetched.
 * @property {Array<App.OfferInteraction | App.SearchInteraction>} interactions - The filtered user interactions.
 */
type ReturnType = {
  fetching: boolean
  interactions: Array<App.OfferInteraction | App.SearchInteraction>
}

/**
 * React hook to fetch and filter user interactions of a specific type.
 *
 * Dispatches an action to fetch user interactions of the given type, and optionally filters them to only include those within the last `sinceDaysAgo` days.
 *
 * @param {Props} props - The props for the hook.
 * @param {App.UserInteractionType} props.interactionType - The type of user interaction to fetch.
 * @param {number} [props.sinceDaysAgo] - Optionally filter interactions to only those since this many days ago.
 * @returns {ReturnType} An object containing the fetching state and the filtered user interactions.
 */
export function useUserInteractions({ interactionType, sinceDaysAgo, disabled = false }: Props): ReturnType {
  const dispatch = useAppDispatch()
  const interactionState = useAppSelector((state) => state.recommendations.userInteractions[interactionType])
  const { finalUserInteractions, fetching } = useMemo(() => {
    if (disabled) {
      return {
        finalUserInteractions: [],
        fetching: false,
      }
    }

    const userInteractions = interactionState?.interactions ?? []
    const fetching = interactionState?.state === 'loading'

    let finalUserInteractions = userInteractions

    if (sinceDaysAgo) {
      const sinceDateMs = Date.now() - sinceDaysAgo * 24 * 60 * 60 * 1000
      finalUserInteractions = userInteractions.filter((interaction) => {
        const interactionDateMs = interaction.interaction_time * 1000
        return interactionDateMs >= sinceDateMs
      })
    }

    return { finalUserInteractions, fetching }
  }, [interactionState, sinceDaysAgo, disabled])

  useEffect(() => {
    if (!disabled) {
      dispatch(fetchUserInteractions(interactionType))
    }
  }, [dispatch, interactionType, disabled])

  return { fetching, interactions: finalUserInteractions }
}
